<script setup lang="ts">
import MissionCardPreview from './MissionCardPreview.vue'
import MissionIconField from './MissionIconField.vue'
import { mirrorMissionFormalFrench } from './missionText'
import { ComboboxSelect } from '@/ui/combobox'

import type { MissionInstance } from '../composables/missionInstances'
import type { WebappMission } from '@/types/types'

const { t, locale } = useI18n()
const router = useRouter()

const props = defineProps<{
  instances: MissionInstance[]
  isLoadingInstances?: boolean
  instancesError?: boolean
  color?: string
  pointsLabel?: string
}>()

const emit = defineEmits<{ (e: 'saved'): void; (e: 'refreshInstances'): void }>()

const isOpen = defineModel<boolean>('open', { required: true })
const mission = defineModel<WebappMission>({ required: true })

const draft = ref<WebappMission | null>(null)
const baseline = ref<WebappMission | null>(null)

watch(
  isOpen,
  (open) => {
    if (!open) return
    draft.value = JSON.parse(JSON.stringify(mission.value))
    baseline.value = JSON.parse(JSON.stringify(mission.value))
  },
  { immediate: true }
)

const hasInstance = computed(() => ['survey', 'contest_game'].includes(draft.value?.type ?? ''))
const isSurvey = computed(() => draft.value?.type === 'survey')
const supportsPoints = computed(() => ['survey', 'contest_game', 'review', 'crm'].includes(draft.value?.type ?? ''))

const translated = (field: 'title' | 'subtitle') =>
  computed({
    get: () => draft.value?.[field]?.[locale.value] ?? '',
    set: (value: string) => {
      if (!draft.value) return
      draft.value = { ...draft.value, [field]: { ...(draft.value[field] ?? {}), [locale.value]: value } }
    }
  })

const title = translated('title')
const subtitle = translated('subtitle')

const MAX_POINTS = 100000

const pointsInputKey = ref(0)

const points = computed({
  get: () => draft.value?.points ?? 0,
  set: (value: number) => {
    if (!draft.value) return
    draft.value = { ...draft.value, points: Math.min(MAX_POINTS, Math.max(0, Math.floor(Number(value) || 0))) }
  }
})

const syncPointsInput = () => {
  pointsInputKey.value++
}

const createInstanceUrl = computed(
  () =>
    router.resolve({
      name: draft.value?.type === 'survey' ? 'SurveyCreate' : 'ContestCreate'
    }).href
)

const instanceLabelKey = computed(() =>
  draft.value?.type === 'survey'
    ? 'pages.loyaltySupports.webapp.missions.survey'
    : 'pages.loyaltySupports.webapp.missions.contestGame'
)

const ALL_SURVEYS = 0

const instanceOptions = computed(() => {
  const options = props.instances.map((instance) => ({ value: instance.id, label: instance.label }))

  return isSurvey.value
    ? [{ value: ALL_SURVEYS, label: t('pages.loyaltySupports.webapp.missions.allSurveys') }, ...options]
    : options
})

const instanceModel = computed({
  get: () => (isSurvey.value && !draft.value?.instanceId ? ALL_SURVEYS : (draft.value?.instanceId ?? null)),
  set: (value: number | null) => {
    if (!draft.value) return
    draft.value.instanceId = value === ALL_SURVEYS ? null : value
  }
})

const emptyInstanceKey = computed(() =>
  draft.value?.type === 'survey'
    ? 'pages.loyaltySupports.webapp.missions.noSurveyAvailable'
    : 'pages.loyaltySupports.webapp.missions.noContestAvailable'
)

const createInstanceKey = computed(() =>
  draft.value?.type === 'survey'
    ? 'pages.loyaltySupports.webapp.missions.createSurvey'
    : 'pages.loyaltySupports.webapp.missions.createContest'
)

const instanceError = computed(() => {
  if (!hasInstance.value || isSurvey.value || draft.value?.instanceId) return ''

  return t('pages.loyaltySupports.webapp.missions.instanceRequired')
})

const canSave = computed(() => !instanceError.value && !(hasInstance.value && props.isLoadingInstances))

const save = () => {
  if (!canSave.value || !draft.value) return

  if (hasInstance.value) {
    draft.value.instanceType = isSurvey.value ? (draft.value.instanceId ? 'survey' : null) : 'contest_game'
  }

  mission.value = baseline.value ? mirrorMissionFormalFrench(draft.value, baseline.value) : draft.value
  isOpen.value = false
  emit('saved')
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent class="sm:max-w-4xl" :show-close-button="true">
      <DialogHeader>
        <DialogTitle>{{ t('pages.loyaltySupports.webapp.missions.editTitle') }}</DialogTitle>
      </DialogHeader>

      <div v-if="draft" class="grid gap-8 md:grid-cols-2">
        <div class="flex flex-col gap-5">
          <div class="flex flex-col gap-2">
            <Label for="missionTitle">{{ t('pages.loyaltySupports.webapp.missions.title') }}</Label>
            <Input id="missionTitle" v-model="title" />
          </div>

          <div class="flex flex-col gap-2">
            <Label for="missionSubtitle">{{ t('pages.loyaltySupports.webapp.missions.subtitle') }}</Label>
            <Input id="missionSubtitle" v-model="subtitle" />
          </div>

          <MissionIconField v-model:icon-type="draft.iconType" v-model:icon-value="draft.iconValue" />

          <div v-if="supportsPoints" class="flex flex-col gap-2">
            <Label for="missionPoints">{{ t('pages.loyaltySupports.webapp.missions.points') }}</Label>
            <Input
              id="missionPoints"
              :key="pointsInputKey"
              v-model="points"
              type="number"
              min="0"
              :max="MAX_POINTS"
              step="1"
              @blur="syncPointsInput"
            />
          </div>

          <div v-if="hasInstance" class="flex flex-col gap-2">
            <Label for="missionInstance">
              {{ t(instanceLabelKey) }}
              <span v-if="!isSurvey" class="text-destructive">*</span>
            </Label>
            <p v-if="isLoadingInstances" class="text-sm text-muted-foreground">{{ t('global.words.loading') }}</p>

            <ComboboxSelect
              v-else-if="instanceOptions.length"
              id="missionInstance"
              v-model="instanceModel"
              :options="instanceOptions"
              :placeholder="t('pages.loyaltySupports.webapp.missions.instancePlaceholder')"
              :search-threshold="6"
              trigger-class="w-full"
            />

            <div
              v-if="!isLoadingInstances && !instances.length"
              class="border-input flex flex-col gap-1 rounded-md border border-dashed p-3"
            >
              <span v-if="instancesError" class="text-destructive text-sm">
                {{ t('pages.loyaltySupports.webapp.missions.instancesLoadError') }}
              </span>
              <span v-else class="text-sm text-muted-foreground">{{ t(emptyInstanceKey) }}</span>
              <div class="flex items-center gap-3">
                <a :href="createInstanceUrl" target="_blank" rel="noopener" class="text-primary text-sm underline">
                  {{ t(createInstanceKey) }}
                </a>
                <button type="button" class="text-sm text-muted-foreground underline" @click="emit('refreshInstances')">
                  {{ t('pages.loyaltySupports.webapp.missions.refreshInstances') }}
                </button>
              </div>
            </div>

            <span v-if="instanceError" class="text-destructive text-sm">{{ instanceError }}</span>
          </div>
        </div>

        <div class="flex items-center justify-center overflow-hidden rounded-xl">
          <MissionCardPreview :mission="draft" :color="color" :points-label="pointsLabel" :locale="locale" />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="isOpen = false">{{ t('global.words.cancel') }}</Button>
        <Button variant="default-primary" :disabled="!canSave" @click="save">
          {{ t('global.words.save') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
